import {
  type MouseEvent,
  type TouchEvent,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";

interface LensState {
  visible: boolean;
  x: number;
  y: number;
  relX: number;
  relY: number;
  width: number;
  height: number;
}

interface UseLensProps {
  zoomFactor: number;
  lensSize: number;
  disabled: boolean;
  rotation?: number;
  onInteractionChange?: (isInteracting: boolean) => void;
}

export const useLens = ({
  zoomFactor,
  lensSize,
  disabled,
  rotation = 0,
  onInteractionChange
}: UseLensProps) => {
  const [lensState, setLensState] = useState<LensState>({
    visible: false,
    x: 0,
    y: 0,
    relX: 0,
    relY: 0,
    width: 0,
    height: 0
  });

  const [currentZoom, setCurrentZoom] = useState(zoomFactor);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setCurrentZoom(zoomFactor);
  }, [zoomFactor]);

  useEffect(() => {
    onInteractionChange?.(lensState.visible);
  }, [lensState.visible, onInteractionChange]);

  const updateLensPosition = useCallback(
    (clientX: number, clientY: number) => {
      if (!imageRef.current) return;

      const rect = imageRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      // Get natural image dimensions
      const naturalWidth = imageRef.current.naturalWidth;
      const naturalHeight = imageRef.current.naturalHeight;

      if (naturalWidth === 0 || naturalHeight === 0) return;

      // Calculate rendered size more reliably
      // Use offsetWidth/offsetHeight which are more stable than clientWidth/clientHeight
      // These give the actual element size excluding transforms
      let renderedWidth = imageRef.current.offsetWidth;
      let renderedHeight = imageRef.current.offsetHeight;

      // Fallback: if offsetWidth/Height are 0 or invalid, calculate from bounding rect
      // accounting for rotation
      if (renderedWidth === 0 || renderedHeight === 0) {
        const rotationAngleRad = Math.abs((rotation * Math.PI) / 180);
        const rotationCos = Math.cos(rotationAngleRad);
        const rotationSin = Math.sin(rotationAngleRad);
        const naturalAspect = naturalWidth / naturalHeight;

        // For rotated images, the bounding box is larger than the actual image
        // Calculate actual image dimensions from bounding box
        if (rotation === 0 || rotation === 180 || rotation === -180) {
          renderedWidth = rect.width;
          renderedHeight = rect.height;
        } else if (
          rotation === 90 ||
          rotation === -90 ||
          rotation === 270 ||
          rotation === -270
        ) {
          renderedWidth = rect.height;
          renderedHeight = rect.width;
        } else {
          // For arbitrary rotations, solve the inverse rotation transform
          // Given: bboxW = w*|cos(θ)| + h*|sin(θ)|, bboxH = w*|sin(θ)| + h*|cos(θ)|
          // And: w/h = naturalAspect, so w = naturalAspect * h
          // Solving the system of equations:
          const cos = Math.abs(rotationCos);
          const sin = Math.abs(rotationSin);

          // From: bboxW = aspect*h*cos + h*sin = h*(aspect*cos + sin)
          //      bboxH = aspect*h*sin + h*cos = h*(aspect*sin + cos)
          // Solve for h using the equation with the larger denominator for numerical stability
          const denominator1 = naturalAspect * cos + sin;
          const denominator2 = naturalAspect * sin + cos;

          if (denominator1 > 0 && denominator2 > 0) {
            // Use the equation with the larger denominator for better numerical stability
            if (denominator1 >= denominator2) {
              renderedHeight = rect.width / denominator1;
            } else {
              renderedHeight = rect.height / denominator2;
            }
            renderedWidth = naturalAspect * renderedHeight;
          } else if (denominator1 > 0) {
            renderedHeight = rect.width / denominator1;
            renderedWidth = naturalAspect * renderedHeight;
          } else if (denominator2 > 0) {
            renderedHeight = rect.height / denominator2;
            renderedWidth = naturalAspect * renderedHeight;
          } else {
            // Fallback for degenerate case (shouldn't happen in practice)
            renderedWidth = rect.width;
            renderedHeight = rect.height;
          }
        }
      }

      // Calculate scale factor from rendered size to natural size
      const scale = renderedWidth / naturalWidth;

      // Transform mouse coordinates to account for rotation
      const angleRad = (-rotation * Math.PI) / 180;
      const cos = Math.cos(angleRad);
      const sin = Math.sin(angleRad);

      // Translate to origin (center of bounding box, which is also image center)
      const translatedX = clientX - centerX;
      const translatedY = clientY - centerY;

      // Rotate back to unrotated coordinates
      const unrotatedX = translatedX * cos - translatedY * sin;
      const unrotatedY = translatedX * sin + translatedY * cos;

      // Calculate relative position on the unrotated image
      const relX = unrotatedX / scale + naturalWidth / 2;
      const relY = unrotatedY / scale + naturalHeight / 2;

      // Check if mouse is within the displayed image bounds
      // Use bounding rect for bounds checking (more forgiving and accurate)
      // Add small tolerance to account for rounding errors and edge cases
      const tolerance = 2;
      const halfBoundingWidth = rect.width / 2;
      const halfBoundingHeight = rect.height / 2;

      // Check against bounding rect (more reliable across screen sizes)
      // Also verify against calculated rendered dimensions as secondary check
      const halfRenderedWidth = renderedWidth / 2;
      const halfRenderedHeight = renderedHeight / 2;

      const isWithinBoundingRect =
        Math.abs(translatedX) <= halfBoundingWidth + tolerance &&
        Math.abs(translatedY) <= halfBoundingHeight + tolerance;

      const isWithinRenderedImage =
        Math.abs(unrotatedX) <= halfRenderedWidth + tolerance &&
        Math.abs(unrotatedY) <= halfRenderedHeight + tolerance;

      if (isWithinBoundingRect && isWithinRenderedImage) {
        setLensState({
          visible: true,
          x: clientX - lensSize / 2,
          y: clientY - lensSize / 2,
          relX: relX,
          relY: relY,
          width: naturalWidth,
          height: naturalHeight
        });
      } else {
        setLensState((prev) => ({ ...prev, visible: false }));
      }
    },
    [lensSize, rotation]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (disabled) return;
      updateLensPosition(e.clientX, e.clientY);
    },
    [disabled, updateLensPosition]
  );

  const handleMouseLeave = useCallback(() => {
    if (!disabled) {
      setLensState((prev) => ({ ...prev, visible: false }));
    }
  }, [disabled]);

  const handleTouchMove = useCallback(
    (e: TouchEvent<HTMLDivElement>) => {
      if (disabled) return;
      // Scrolling is prevented via 'touch-action: none' CSS on the container
      const touch = e.touches[0];
      updateLensPosition(touch.clientX, touch.clientY);
    },
    [disabled, updateLensPosition]
  );

  const handleTouchStart = useCallback(
    (e: TouchEvent<HTMLDivElement>) => {
      if (disabled) return;
      const touch = e.touches[0];
      updateLensPosition(touch.clientX, touch.clientY);
    },
    [disabled, updateLensPosition]
  );

  const handleTouchEnd = useCallback(() => {
    if (!disabled) {
      setLensState((prev) => ({ ...prev, visible: false }));
    }
  }, [disabled]);

  return {
    lensState,
    currentZoom,
    imageRef,
    containerRef,
    handleMouseMove,
    handleMouseLeave,
    handleTouchMove,
    handleTouchStart,
    handleTouchEnd
  };
};
